import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import lineArrow from '../../../../../assets/svgs/connect-line-arrow-right.svg';
import lineCurve from '../../../../../assets/svgs/connect-line-curve.svg';
import { checkTotalDealCount } from '../../../../../hooks/Inventory/UseBulkInventory';
import { theme } from '../../../../../styles/theme';
import { Icon } from '../../../../style/Icon';

type ReplaceChangeModalProps = {
  isOpen: boolean;
  closeModal: Dispatch<SetStateAction<boolean>>;
  submitChange: Dispatch<SetStateAction<boolean>>;
  data: any;
};
const ReplaceChangeModal: FC<ReplaceChangeModalProps> = (props) => {
  const [dealData, SetDealData] = useState<any>({});

  useEffect(() => {
    let leaseDeal = 0;
    let financeDeal = 0;
    let cashDeal = 0;

    if (props.data.bulkData.isAllRowsSelected) {
      props.data.bulkData.dealerList.forEach((e) => {
        if (e.dealType === 1 && e.dealerId === props.data.bulkData.selectedDealer[0]) {
          leaseDeal = e.dealsCount;
        }
        if (e.dealType === 2 && e.dealerId === props.data.bulkData.selectedDealer[0]) {
          financeDeal = e.dealsCount;
        }
        if (e.dealType === 4 && e.dealerId === props.data.bulkData.selectedDealer[0]) {
          cashDeal = e.dealsCount;
        }
      });
    } else {
      props.data.bulkData.dealerList.forEach((e) => {
        if (e.dealType.toLowerCase() === 'lease') leaseDeal++;
        if (e.dealType.toLowerCase() === 'finance') financeDeal++;
        if (e.dealType.toLowerCase() === 'cash') cashDeal++;
      });
    }

    const data = { lease: leaseDeal, finance: financeDeal, cash: cashDeal };
    SetDealData(data);
  }, []);

  const closeConfirmationModal = () => {
    props.closeModal(false);
  };

  const submitChanges = () => {
    props.submitChange(true);
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={closeConfirmationModal}
      style={modalStyles}
      contentLabel='Save Modal'
      ariaHideApp={false}
      data-testid='replace-change-modal'
    >
      <ModalBodyWrapper>
        <div className={'title-text'}>Submit these changes?</div>
        <div className={'sub-title-text'}>
          Please confirm that you have made all necessary changes before submitting. After
          submission your predictions will be live within a few minutes.
        </div>
        <div className={'mail-text'}>
          <Icon
            type='mail'
            additionalClassName='am-icon'
            size={theme.typography.h3}
            data-testid='mail-icon'
          />
          You will receive an email when updates are complete.
        </div>
        <div className={'desc-text'}>
          Please be aware, for changes to be reflected in the predictive campaigns you must submit{' '}
          <b>before the communicated deadline</b>. Contact your Dealer Relations Manager for more
          information.
        </div>
        <table>
          <tbody>
            <tr className={'table-row-ib'} style={{ height: '20px' }}>
              <td
                className={
                  ((!props.data.vehicle.isAvailableForReplacementLease &&
                    props.data.vehicle.stockType.toUpperCase() === 'SAMPLE') ||
                    props.data.vehicle.isUsed) &&
                  dealData.lease > 0
                    ? 'strike-text deal-text'
                    : 'deal-text'
                }
              >
                <b>{dealData.lease}</b> Lease Deals
              </td>
              <td>
                <img src={lineArrow} alt='logo' />
              </td>
              <td rowSpan={3} style={{ width: '160px', marginLeft: '12px' }}>
                <span className={'condition'}>
                  {props.data.vehicle?.vehicleType !== null
                    ? props.data.vehicle?.vehicleType
                    : 'Sample'}
                </span>{' '}
                {props.data.vehicle.year} {props.data.vehicle.brandName} {props.data.vehicle.trim}
                <br />
                <b>{checkTotalDealCount(props.data.vehicle, dealData)}</b> total replacements
              </td>
            </tr>
            <tr className={'table-row-ib'}>
              <td
                style={{ marginTop: '6px' }}
                className={
                  !props.data.vehicle.isAvailableForReplacementFinance &&
                  props.data.vehicle.stockType.toUpperCase() === 'SAMPLE' &&
                  dealData.finance > 0
                    ? 'strike-text deal-text'
                    : 'deal-text'
                }
              >
                <b>{dealData.finance}</b> Finance Deals
              </td>
              <td className={'first-img'}>
                <img src={lineCurve} alt='logo' />
              </td>
            </tr>
            <tr className={'table-row-ib'}>
              <td
                style={{ marginTop: '-14px' }}
                className={
                  !props.data.vehicle.isAvailableForReplacementCash &&
                  props.data.vehicle.stockType.toUpperCase() === 'SAMPLE' &&
                  dealData.cash > 0
                    ? 'strike-text deal-text'
                    : 'deal-text'
                }
              >
                <b>{dealData.cash}</b> Cash Deals
              </td>
              <td className={'second-img'}>
                <img style={{ width: '117px' }} src={lineCurve} alt='logo' />
              </td>
            </tr>
          </tbody>
        </table>
      </ModalBodyWrapper>
      {props.data.isWarning && (
        <WarningMsg>
          <Icon
            type='warning'
            additionalClassName='am-icon'
            size={theme.typography.h3}
            data-testid='warning-icon'
          />
          <span>Not all deal types are eligible for this vehicle</span>
        </WarningMsg>
      )}

      <Footer>
        <FooterButtonPanel>
          <Button onClick={closeConfirmationModal} className={'cancel'}>
            CANCEL
          </Button>
          <Button onClick={submitChanges} className={'save-button'}>
            SUBMIT CHANGES
          </Button>
        </FooterButtonPanel>
      </Footer>
    </Modal>
  );
};

export default ReplaceChangeModal;

const modalStyles = {
  content: {
    top: '10%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, 2%)',
    width: '660px',
    minHeight: '235px',
    maxHeight: '640px',
    zIndex: 100000,
    padding: '0',
    borderRadius: '8px',
  },
};

const ModalBodyWrapper = styled.div`
  padding: 40px;
  .title-text {
    margin-bottom: 15px;
    font-size: 20px;
    font-weight: 600;
  }
  .sub-title-text {
    margin-bottom: 10px;
    font-weight: 600;
  }
  .mail-text {
    margin-bottom: 10px;
    span {
      margin-right: 5px;
    }
  }
  .desc-text {
    margin-bottom: 10px;
  }
  .strike-text {
    text-decoration: line-through;
    color: #a3a3a3;
  }
  .table-row-ib {
    display: flex;
    flex-direction: row;
  }
  .first-img {
    margin-top: -8px;
  }
  .second-img {
    margin-top: -27px;
  }
  .deal-text {
    width: 130px;
  }
  .condition {
    border-radius: 3px;
    background-color: ${theme.palette.dustyTwo};
    padding: 4px;
    margin-right: 5px;
    vertical-align: text-top;
    color: ${theme.palette.white};
    font-weight: 600;
  }
  .new {
    background-color: $am-green;
  }
`;

const WarningMsg = styled.div`
  padding: 10px 0px;
  padding-left: 35px;
  background-color: #fff9dc;
  color: #8a6d3b;
  .am-icon-warning {
    margin-right: 20px;
  }
`;

const FooterButtonPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  margin-right: 20px;
  margin-left: 22px;
  .cancel {
    background-color: #ddd;
    border-bottom: 1px solid silver;
    color: #333;
    :hover {
      cursor: pointer;
      background-color: ${theme.palette.backgroundGray};
    }
  }
  .save-button {
    background-color: #593487;
    color: #fff;
    border: 1px solid #593487;
    :hover {
      cursor: pointer;
      background-color: #775ea5;
    }
  }
`;

const Button = styled.button`
  border-radius: 3px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  height: 35px;
  margin-left: 15px;
  padding: 0px 10px;
  border: 0px;
  &:disabled {
    background-color: ${theme.palette.gainsboro};
  }
`;

const Footer = styled.div`
  background-color: ${theme.palette.white};
  border-top: 1px solid ${theme.palette.background};
  width: 100%;
`;
